import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';

import type { APIData } from 'webapp-types';
import { copyObject } from 'webapp/utils/common';

import type CustomMetricSetupFormItemField from 'webapp/models-custom/custom-metric-setup-form/item/field';
import CustomMetricSetupFormPrebuilt from 'webapp/models-custom/custom-metric-setup-form/prebuilt';
import type { SourceOption } from 'webapp/models-custom/source-type';

export enum DatasetFieldId {
	MEASURE = 'measure',
	DATE = 'date',
	DIMENSIONS = 'dimensions',
}

export default class CustomMetricSetupFormDataset extends CustomMetricSetupFormPrebuilt {
	@tracked sourceId?: number = undefined;
	@tracked sourceTypeId: number | null = null;
	@tracked selectedSourceOption: SourceOption | undefined = undefined;
	@tracked isTypeIngest: boolean | undefined = undefined;

	@tracked pagination: { page: number; pageSize: number; totalItems: number } | null = null;
	@tracked schema: APIData.CustomQuery.DatasetSchema | null = null;
	@tracked data: APIData.CustomQuery.DatasetRow[] | null = null;
	@tracked updatedOn: string | null = null;
	@tracked selected: {
		[DatasetFieldId.MEASURE]: string | null;
		[DatasetFieldId.DATE]: string | null;
		[DatasetFieldId.DIMENSIONS]: string[];
	} = {
		[DatasetFieldId.MEASURE]: null,
		[DatasetFieldId.DATE]: null,
		[DatasetFieldId.DIMENSIONS]: [],
	};

	override get isWizardFlowSupported(): boolean {
		return false;
	}

	override get isPreviewButtonDisabled(): boolean {
		const isPreviewStillLoading = this.isLoadingPreview && !this.isPreviewTakingTooLong;
		return super.isPreviewButtonDisabled || this.isLoadingBuilder || isPreviewStillLoading;
	}

	override get isSaveButtonDisabled(): boolean {
		return super.isSaveButtonDisabled || !this.isValid;
	}

	override initData(data: APIData.CustomQuery.BuilderDatasetResponse) {
		this.selected = {
			[DatasetFieldId.MEASURE]: data.settings?.measure.id ?? null,
			[DatasetFieldId.DATE]: data.settings?.date.id ?? null,
			[DatasetFieldId.DIMENSIONS]:
				data.settings?.dimensions.map((dimension) => dimension.id) ?? [],
		};

		this.pagination = data.pagination;
		this.schema = data.schema;
		this.updatedOn = data.updatedOn;
		this.data = data.data;

		const preselectedColumns = this.preselectedColumns;
		const hiddenColumns = this.schema?.items
			.filter((column) => column.isHidden)
			.map((column) => column.columnName);

		const builderData = [...data.builder];
		//we need to filter out the items that are hidden
		builderData.forEach((builderItem) => {
			const items = builderItem.items;
			for (const item of items) {
				const filteredItems =
					item.items?.filter((subItem: SomeDataObject) => {
						const isPreselected = preselectedColumns.includes(subItem.id);
						const isHidden = hiddenColumns?.includes(subItem.id);

						if (isPreselected) {
							return true;
						}

						if (!isHidden) {
							return true;
						}

						return false;
					}) || [];

				item.items = [...filteredItems];
			}
		});

		this.originalSettings = {
			builder: builderData,
			settings: data.settings,
		};

		super.initData(data);
	}

	@action
	override handleItemAction(item: CustomMetricSetupFormItemField, action: string): void {
		if (action === 'change') {
			const columnName = item.value as string;
			const fieldId = item.id;
			this.selected = copyObject({ ...this.selected, [fieldId]: columnName });

			if (typeof item !== 'string' && item.id === 'dimensions') {
				this.checkToResetSelectedPreviewDimension(item);
			}
		}

		super.handleItemAction(item, action);
	}

	override onQueryPreviewFinished(reply: APIData.CustomQuery.PreviewResponse) {
		this.data = reply.data ?? [];
	}

	get preselectedColumns(): string[] {
		const columns: string[] = [];

		if (this.selected[DatasetFieldId.MEASURE]) {
			columns.push(this.selected[DatasetFieldId.MEASURE]);
		}

		if (this.selected[DatasetFieldId.DATE]) {
			columns.push(this.selected[DatasetFieldId.DATE]);
		}

		this.selected[DatasetFieldId.DIMENSIONS].forEach((dimension: string) => {
			columns.push(dimension);
		});

		return columns;
	}
}
